/**
 * Vendor Sign-Up — self-signup form. Creates a vendor row with
 * status='pending'. Admin must activate before the vendor can log in.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Building2, Mail, Lock, User as UserIcon, Phone, CheckCircle2, Copy } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { vendorSignup } from "@/lib/vendorApi";
import { toast } from "sonner";

export default function VendorSignUp() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", password: "", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit =
    form.name.trim().length > 0 &&
    form.business_name.trim().length > 0 &&
    form.email.trim().length > 3 &&
    form.password.length >= 8 &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await vendorSignup({
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim() || undefined,
      });
      setSuccess(res);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create vendor account.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <AuthShell
        title="Application received"
        subtitle="Our team reviews new vendors within 24 hours. You'll receive an email when your account is activated."
        footer={
          <span>
            <Link to="/vendor/login" data-testid="vendor-signup-goto-login" className="text-[#D4AF37] hover:text-[#F3CE5A]">
              Return to sign in →
            </Link>
          </span>
        }
      >
        <div className="mt-8 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
          <p className="text-sm text-white/80 font-medium">Your referral code</p>
          <div className="mt-3 flex items-center justify-center gap-2">
            <code className="font-mono text-lg text-[#F3CE5A] tracking-widest bg-black/40 px-3 py-1.5 rounded">
              {success.referral_code}
            </code>
            <button
              type="button"
              data-testid="vendor-signup-copy-code"
              onClick={() => {
                navigator.clipboard.writeText(success.referral_code);
                toast.success("Code copied");
              }}
              className="text-white/60 hover:text-white transition p-1.5"
              title="Copy code"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-white/45 mt-4 leading-relaxed">
            Save this code. Share links like<br />
            <code className="text-white/70">facetra.studio/signup?ref={success.referral_code}</code><br />
            after activation to start earning 20% commission on first purchase.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Become a Facetra partner"
      subtitle="Earn 20% commission on the first purchase of every jeweller you refer to Facetra. Approvals are manual — expect a response within 24 hours."
      footer={
        <span>
          Already a partner?{" "}
          <Link to="/vendor/login" data-testid="vendor-signup-signin-link" className="text-[#D4AF37] hover:text-[#F3CE5A]">
            Sign in →
          </Link>
        </span>
      }
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="vendor-signup-form">
        <Field
          icon={<UserIcon className="w-4 h-4" />}
          label="Your name"
          value={form.name}
          onChange={on("name")}
          testId="vendor-signup-name"
        />
        <Field
          icon={<Building2 className="w-4 h-4" />}
          label="Business name"
          value={form.business_name}
          onChange={on("business_name")}
          testId="vendor-signup-business"
        />
        <Field
          icon={<Mail className="w-4 h-4" />}
          label="Email"
          type="email"
          value={form.email}
          onChange={on("email")}
          testId="vendor-signup-email"
        />
        <Field
          icon={<Phone className="w-4 h-4" />}
          label="Phone (optional)"
          value={form.phone}
          onChange={on("phone")}
          testId="vendor-signup-phone"
        />
        <Field
          icon={<Lock className="w-4 h-4" />}
          label="Password (min 8 chars)"
          type="password"
          value={form.password}
          onChange={on("password")}
          testId="vendor-signup-password"
        />

        {error && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2" data-testid="vendor-signup-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="vendor-signup-submit"
          className="w-full mt-2 rounded-full bg-gradient-to-r from-[#F3CE5A] to-[#D4AF37] hover:from-[#F5D875] hover:to-[#E4C048] disabled:opacity-40 disabled:cursor-not-allowed text-black font-medium py-3 text-sm tracking-wide transition-all shadow-[0_0_20px_rgba(212,175,55,0.28)] flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          Submit application
        </button>
      </form>
    </AuthShell>
  );
}

function Field({ icon, label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-white/50 flex items-center gap-1.5 mb-1.5">
        {icon} {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-black/40 border border-white/10 focus:border-[#D4AF37]/60 focus:outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 transition"
      />
    </label>
  );
}
